import { AuthRoleIdEnum, AuthRoleNameEnum } from 'constants/user'
import { MembershipInfo } from 'types/auth'

const ROLE_ID_TO_NAME: Record<AuthRoleIdEnum, AuthRoleNameEnum> = {
  [AuthRoleIdEnum.OWNER]: AuthRoleNameEnum.OWNER,
  [AuthRoleIdEnum.ADMIN]: AuthRoleNameEnum.ADMIN,
  [AuthRoleIdEnum.COACH]: AuthRoleNameEnum.COACH,
  [AuthRoleIdEnum.PLAYER]: AuthRoleNameEnum.PLAYER
}

export const getRoleNameFromId = (
  roleId?: number | null
): AuthRoleNameEnum | undefined => {
  if (roleId === undefined || roleId === null) return undefined
  return ROLE_ID_TO_NAME[roleId as AuthRoleIdEnum]
}

export const getRoleIdFromMembership = (
  membership?: MembershipInfo | null,
  fallbackRoleId: AuthRoleIdEnum = AuthRoleIdEnum.PLAYER
): AuthRoleIdEnum => {
  if (membership?.roleId === undefined || membership?.roleId === null) {
    return fallbackRoleId
  }
  return membership.roleId
}

export const isBasicUserByMembership = (
  membership?: MembershipInfo | null,
  fallbackRoleId: AuthRoleIdEnum = AuthRoleIdEnum.PLAYER
): boolean => getRoleIdFromMembership(membership, fallbackRoleId) === AuthRoleIdEnum.PLAYER

export const isOrgAdminByMembership = (
  membership?: MembershipInfo | null,
  fallbackRoleId: AuthRoleIdEnum = AuthRoleIdEnum.PLAYER
): boolean => getRoleIdFromMembership(membership, fallbackRoleId) === AuthRoleIdEnum.ADMIN

export const isOrgOwnerByMembership = (
  membership?: MembershipInfo | null
): boolean => membership?.roleId === AuthRoleIdEnum.OWNER

/** Owner outranks Admin; both have admin-level org powers. */
export const isAdminOrOwner = (roleId?: number | null): boolean =>
  roleId === AuthRoleIdEnum.OWNER || roleId === AuthRoleIdEnum.ADMIN

/** Owner > Admin > Coach > Player. Returns 0..3 (lower = higher rank). */
export const getRoleRank = (roleId?: number | null): number => {
  switch (roleId) {
    case AuthRoleIdEnum.OWNER: return 0
    case AuthRoleIdEnum.ADMIN: return 1
    case AuthRoleIdEnum.COACH: return 2
    case AuthRoleIdEnum.PLAYER: return 3
    default: return 99
  }
}

/**
 * True when `roleId` meets or exceeds `min` on the Owner > Admin > Coach > Player ladder.
 * Direct numeric compare on AuthRoleIdEnum is wrong (OWNER=4 numerically beats ADMIN=1
 * even though OWNER outranks ADMIN). Always use this helper for rank comparisons.
 */
export const meetsMinRole = (
  roleId: number | null | undefined,
  min: AuthRoleIdEnum
): boolean => getRoleRank(roleId) <= getRoleRank(min)
